// Подписи журнала действий для людей — одни для экрана журнала и для выгрузки в Excel (сценарий
// 4.3, FR-033): действия, операции матрицы прав (у отказов), итог, откуда, типы записей, поля
// «было — стало» и их значения. Термины — по словарю docs/glossary.md (FR-036б); тексты не
// зависят от рода человека (FR-036а). Перенесено из apps/web/src/app/labels.ts.
import type { AreaKind } from '../access/area.ts';
import type { SubstitutionState } from '../access/assignment.ts';
import type { SupportGrantState } from '../access/support-grant.ts';
import type { UserState } from '../access/user.ts';
import { isOperation } from '../rights/matrix.ts';
import type { Operation } from '../rights/matrix.ts';
import type { ActorKind, AuditAction, AuditEntry, AuditResult, AuditSource } from './entry.ts';

/** Действия журнала (auditActions) — прошедшее время, без рода человека. */
export const auditActionLabels = {
  'paste.analysis_started': 'Запущен разбор вставки',
  'paste.analysis_cancelled': 'Разбор вставки отменён',
  'paste.decisions_confirmed': 'Подтверждены назначения и подзадачи',

  'auth.login': 'Вход',
  'auth.login_failed': 'Неудачная попытка входа',
  'auth.logout': 'Выход',
  'user.created': 'Создан пользователь',
  'user.updated': 'Изменён пользователь',
  'user.archived': 'Пользователь перенесён в архив',
  'user.restored': 'Пользователь возвращён из архива',
  'user.locked': 'Вход заблокирован',
  'user.unlocked': 'Блокировка снята',
  'user.unlock_undone': 'Снятие блокировки отменено',
  'invite.issued': 'Выпущена ссылка-приглашение',
  'invite.used': 'Использована ссылка-приглашение',
  'password.reset': 'Пароль сброшен',
  'password.changed': 'Пароль изменён',
  'password.change_failed': 'Неудачная попытка сменить пароль',
  'password.recovery_requested': 'Запрошено восстановление пароля',
  'user.welcome_requested': 'Приветственное письмо отправлено заново',
  'sessions.revoked': 'Сеансы завершены',
  'role.assigned': 'Назначена роль',
  'role.changed': 'Изменено назначение роли',
  'role.removed': 'Роль снята',
  'role.restored': 'Роль возвращена',
  'substitution.created': 'Роль передана на время',
  'substitution.cancelled': 'Замещение отменено',
  'substitution.ended': 'Замещение закончилось',
  'support.granted': 'Выдан доступ сопровождению',
  'support.revoked': 'Доступ сопровождения прекращён',
  'support.expired': 'Доступ сопровождения закончился',
  'audit.exported': 'Журнал выгружен в Excel',
  'job.retried': 'Фоновая задача повторена',
  'job.retry_undone': 'Повтор фоновой задачи отменён',
  'job.cancelled': 'Фоновая задача отменена',
  'audit.export.requested': 'Заказана выгрузка журнала',
  'audit.export.downloaded': 'Скачан файл выгрузки журнала',
  'audit.export.deleted': 'Удалён файл выгрузки журнала',
  'document.manager_changed': 'Изменён менеджер протоколов',
  'document.created': 'Создан документ',
  'document.version_uploaded': 'Загружена версия документа',
  'directory.record_created': 'Создана запись справочника',
  'directory.record_updated': 'Изменена запись справочника',
  'directory.record_archived': 'Запись справочника перенесена в архив',
  'directory.record_restored': 'Запись справочника возвращена из архива',
  'directory.email_set': 'Записана почта',
  'directory.email_confirmed': 'Подтверждён внешний адрес',
  'directory.mail_flag_set': 'Изменён признак «получает письма»',
  'directory.workplace_added': 'Добавлено место работы',
  'directory.workplace_updated': 'Изменено место работы',
  'directory.workplace_removed': 'Снято место работы',
  'directory.curator_added': 'Назначен куратор',
  'directory.curator_updated': 'Изменён основной куратор',
  'directory.curator_removed': 'Снят куратор',
  'directory.zone_set': 'Задана зона ответственности',
  'directory.alias_added': 'Добавлен псевдоним',
  'directory.alias_rejected': 'Псевдоним отклонён',
  'directory.alias_reject_undone': 'Отметка «не он» отменена',
  'directory.mention_resolved': 'Решено упоминание',
  'directory.pair_suggested': 'Предложена пара «тот же?»',
  'directory.pair_decided': 'Решена пара «тот же?»',
  'directory.merged': 'Записи склеены',
  'directory.unmerged': 'Склейка разъединена',
  'directory.kind_changed': 'Вид сменён',
  'directory.kind_change_undone': 'Вид возвращён',
  'directory.kind_reviewed': 'Вид подтверждён',
  'directory.kind_review_undone': 'Подтверждение вида отменено',
  'directory.unit_created': 'Подразделение создано',
  'directory.unit_updated': 'Подразделение изменено',
  'directory.unit_email_set': 'Общая почта подразделения',
  'directory.unit_archived': 'Подразделение в архиве',
  'directory.unit_restored': 'Подразделение возвращено',
  'directory.bulk_applied': 'Массовое действие',
  'directory.bulk_undone': 'Массовое действие отменено',
  'settings.changed': 'Изменена настройка',
  'agent_key.issued': 'Выдан ключ агента',
  'agent_key.revoked': 'Отозван ключ агента',
  'delivery.connection_saved': 'Подключение почты изменено',
  'delivery.key_set': 'Ключ Resend задан',
  'delivery.key_removed': 'Ключ Resend удалён',
  'delivery.sending_switched': 'Отправка писем включена / отключена',
  'delivery.reports_registered': 'Отчёты о доставке настроены',
  'delivery.tracking_disabled': 'Отслеживание писем выключено',
  'delivery.test_letter_sent': 'Пробное письмо',
  'delivery.check_requested': 'Проверка подключения почты',
  'delivery.reports_requested': 'Настройка отчётов о доставке',
  'delivery.reply_received': 'Получено ответное письмо',
  'template.connected': 'Протокол подключён',
  'template.version_created': 'Новая версия схемы',
  'template.renamed': 'Схема переименована',
  'template.archived': 'Схема в архиве',
  'template.restored': 'Схема возвращена из архива',
  'protocol.transferred': 'Протокол переведён в систему',
  'protocol.reverted': 'Протокол возвращён в режим файла',
  'protocol.cells_edited': 'Правка ячеек',
  'protocol.row_inserted': 'Новая строка',
  'protocol.parent_resolved': 'Родитель выбран',
  'protocol.branches_moved': 'Ветви перенесены',
  'protocol.branches_copied': 'Ветви скопированы',
  'protocol.branches_archived': 'Ветви в архиве',
  'protocol.branches_restored': 'Ветви возвращены из архива',
  'protocol.item_split': 'Пункт разделён',
  'protocol.undone': 'Действие отменено',
  'protocol.redone': 'Действие повторено',
  'protocol.cells_batch': 'Пакетная правка ячеек',
  'protocol.cells_styled': 'Оформление ячеек',
  'protocol.rows_hidden': 'Строки скрыты в протоколе',
  'protocol.rows_shown': 'Строки показаны в протоколе',
  'protocol.field_added': 'Столбец добавлен',
  'protocol.field_renamed': 'Столбец переименован',
  'protocol.assignees_set': 'Ответственные назначены',
  'protocol.work_state_set': 'Состояние работы изменено',
  'protocol.created': 'Протокол создан в системе',
  'protocol.week_snapshot': 'Неделя зафиксирована',
  'protocol.exported': 'Протокол выгружен',
  'protocol.cells_bordered': 'Границы ячеек',
  'protocol.cells_merged': 'Объединение ячеек',
  'protocol.borders_backfilled': 'Границы перенесены из файла',
  'protocol.contractors_set': 'Контрагенты назначены',
  'protocol.parties_replaced': 'Ответственные или контрагенты заменены',
  'protocol.parties_backfilled': 'Организации перенесены в «Контрагент»',
  'protocol.parties_moved': 'Ответственные или контрагенты перенесены',
  'protocol.parties_split': 'Ответственный или контрагент разделён',
  'protocol.version_restored': 'Версия восстановлена',
  'protocol.version_reverted': 'Возвращено из версии',
  'protocol.version_named': 'Имя версии изменено',
  'protocol.access_granted': 'Доступ к протоколу выдан',
  'protocol.access_changed': 'Доступ к протоколу изменён',
  'protocol.access_removed': 'Доступ к протоколу снят',
  'protocol.sharing_changed': 'Общий доступ изменён',
  'protocol.access_invited': 'Сотрудник приглашён в протокол',
  // 030: вход по ссылке, «Первое письмо», «Ошибка в пункте…».
  'account.person_linked': 'Учётная запись связана с человеком',
  'login_link.issued': 'Выпущена ссылка входа',
  'login_link.used': 'Вход по ссылке',
  'login_link.revoked': 'Ссылка входа отозвана',
  'login_link.not_me': '«Это не я» по ссылке входа',
  'first_letter.scheduled': '«Первое письмо» запланировано',
  'first_letter.cancelled': '«Первое письмо» отменено',
  'item.link_disputed': 'Назначение оспорено',
  'item.reported': '«Ошибка в пункте…»',
  'item.link_check_decided': 'Решение по связи упоминания',
  // 033: стоп-кран и рассылка протокола.
  'delivery.messages_stopped': 'Все сообщения остановлены',
  'delivery.messages_resumed': 'Сообщения возобновлены',
  'delivery.mailing_changed': 'Рассылка протокола изменена',
  'delivery.meeting_day_changed': 'День планёрки изменён',
  // 032: ответ из письма, отписка, отъезд, копия руководителю.
  'answer_link.issued': 'Выпущена личная ссылка',
  'answer_link.revoked': 'Личная ссылка отозвана',
  'answer.accepted': 'Ответ из письма',
  'answer.repeat': 'Повторный ответ из письма',
  'answer.entered': 'Ответ из письма внёс контролёр',
  'unsubscribe.created': 'Отписка от вида писем',
  'unsubscribe.removed': 'Отписка снята',
  'absence.set': '«В отъезде до» поставлено',
  'absence.cleared': '«В отъезде до» снято',
  'manager_copy.sent': 'Копия руководителю',
} as const satisfies Record<AuditAction, string>;

/** Операции матрицы прав (contracts/rights-matrix.md): так подписан отказ в журнале. */
export const operationLabels = {
  'auth.session': 'Вход, выход, выход на всех устройствах',
  'profile.read': 'Свой профиль',
  'profile.change_password': 'Смена своего пароля',
  'users.read': 'Пользователи: список и карточка',
  'users.create': 'Создание пользователей',
  'users.update': 'Изменение пользователей',
  'users.archive': 'Перенос пользователей в архив',
  'users.restore': 'Возврат пользователей из архива',
  'users.invite': 'Выпуск ссылки-приглашения',
  'users.reset_password': 'Сброс пароля',
  'users.unlock': 'Снятие блокировки входа',
  'users.revoke_sessions': 'Завершение сеансов пользователя',
  'assignments.read': 'Назначения ролей: просмотр',
  'assignments.manage': 'Назначения ролей: создать, изменить, архив, вернуть',
  'substitutions.read': 'Замещения: просмотр',
  'substitutions.create': 'Передача роли на время',
  'substitutions.cancel': 'Отмена замещения',
  'substitutions.candidates': 'Замещения: кандидаты в заместители',
  'support.read': 'Доступ сопровождения: просмотр',
  'support.grant': 'Выдача доступа сопровождению',
  'support.revoke': 'Прекращение доступа сопровождения',
  'rights.read': 'Роли и права',
  'audit.read.content': 'Журнал: записи по документам и пунктам',
  'audit.read.access': 'Журнал: записи о пользователях и доступе',
  'audit.export': 'Выгрузка журнала в Excel',
  'notifications.read': 'Уведомления',
  'ops.read': 'Выкатки и проверки восстановления',
  'ops.health.read': 'Краткая панель состояния',
  'ops.status.read': 'Панель состояния',
  'ops.jobs.read': 'Фоновые задачи: просмотр',
  'ops.jobs.retry': 'Повтор фоновой задачи',
  'ops.jobs.cancel': 'Отмена фоновой задачи',
  'ops.log.read': 'Технический журнал',
  'ops.alerts.read': 'Оповещения сопровождению',
  'ops.backups.read': 'Копии и проверки восстановления',
  'ops.metrics.read': 'Метрики',
  'documents.manage': 'Управление протоколами',
  'documents.create': 'Загрузка нового протокола',
  'documents.versions.upload': 'Загрузка новой версии документа',
  'documents.read': 'Документы: список, оригинал, лист контроля',
  'control.read': 'Таблица открытых пунктов',
  'directory.read': 'Справочник: просмотр',
  'directory.write': 'Справочник: создание и дополнение записей',
  'directory.decide': 'Справочник: пары, склейка, подтверждение адреса',
  'settings.write': 'Настройки установки',
  'agent_keys.manage': 'Ключи агента',
  'mail.read': 'Почта: подключение и письма',
  'mail.write': 'Почта: подключение, ключ и пробное письмо',
  'templates.read': 'Схемы шаблонов: просмотр',
  'templates.write': 'Схемы шаблонов: подключение, правка и архив',
  'minutes.read': 'Протокол в системе: просмотр',
  'minutes.edit': 'Протокол в системе: правка таблицы',
  'minutes.transfer': 'Протокол в системе: перевод и возврат в режим файла',
  'minutes.manage': 'Протокол в системе: доп. поля, «Скрыть в протоколе», снимок недели',
  'minutes.access.read': 'Доступ к протоколу: просмотр',
  'minutes.access.manage': 'Доступ к протоколу: роли, общий доступ, приглашение',
  'items.own': 'Свои пункты: «Мои задачи», ответы и правка своих пунктов',
  'minutes.first_letter': '«Первое письмо» по протоколу',
  'mail.stop': 'Стоп-кран сообщений',
  'minutes.mailing': 'Рассылка протокола',
  'minutes.mailing_enable': 'Включение рассылки протокола',
  'items.team': 'Просроченные пункты своих сотрудников',
  'people.away': '«В отъезде до» другому человеку',
} as const satisfies Record<Operation, string>;

function isAuditAction(value: string): value is AuditAction {
  return Object.hasOwn(auditActionLabels, value);
}

/** Подпись действия журнала; отказ несёт код операции матрицы прав; неизвестное — как есть. */
export function auditOperationLabel(operation: string): string {
  if (isAuditAction(operation)) return auditActionLabels[operation];
  return isOperation(operation) ? operationLabels[operation] : operation;
}

export const auditResultLabels = {
  ok: 'Выполнено',
  denied: 'Отказ',
} as const satisfies Record<AuditResult, string>;

export const auditSourceLabels = {
  web: 'Интерфейс',
  cli: 'Команда на сервере',
  system: 'Сервис',
  agent: 'Агент',
} as const satisfies Record<AuditSource, string>;

/** Автор не пользователь: команда установки на сервере или сам сервис. По подписи тоже ищут (T112). */
export const actorKindLabels = {
  installer: 'Установка',
  system: 'Сервис',
} as const satisfies Record<Exclude<ActorKind, 'user'>, string>;

/** Автор записи: ФИО из текущей записи пользователя (FR-033), иначе установка или сервис. */
export function auditActorName(actor: AuditEntry['actor']): string {
  if (actor.name) return actor.name;
  if (actor.kind !== 'user') return actorKindLabels[actor.kind];
  return actor.userId ?? '';
}

/** Типы записей журнала (entity_type на сервере). */
export const entityTypeLabels: Readonly<Record<string, string>> = {
  user: 'Пользователь',
  assignment: 'Назначение роли',
  substitution: 'Замещение',
  support_grant: 'Доступ сопровождения',
  invite: 'Ссылка-приглашение',
  session: 'Сеанс',
  rights_matrix: 'Матрица прав',
  audit_entry: 'Журнал действий',
  notification: 'Уведомление',
  deployment: 'Выкатка',
  job: 'Фоновая задача',
  audit_export: 'Выгрузка журнала',
  // 002: отказы на экранах сопровождения (kernel/authorize.ts, entityTypeOf).
  service_status: 'Состояние сервиса',
  log_entry: 'Технический журнал',
  alert: 'Оповещение',
  backup: 'Копия базы',
  metrics: 'Метрики',
  // 003: документы и пункты.
  document: 'Документ',
  document_folder: 'Папка протоколов',
  document_manager_node: 'Папка или протокол',
  document_comment: 'Комментарий протокола',
  document_version: 'Версия документа',
  item: 'Пункт',
  // 005: справочник, настройки, ключи агента; directory — отказ в операции справочника.
  directory: 'Справочник',
  person: 'Человек',
  organization: 'Организация',
  email: 'Почта',
  workplace: 'Место работы',
  curatorship: 'Кураторство',
  document_zone: 'Зона ответственности',
  alias: 'Псевдоним',
  mention: 'Упоминание',
  pair: 'Пара «тот же?»',
  merge: 'Склейка',
  kind_change: 'Смена вида',
  unit: 'Подразделение',
  bulk_action: 'Массовое действие',
  setting: 'Настройка',
  agent_key: 'Ключ агента',
  mail_connection: 'Подключение почты',
  mail_message: 'Письмо',
  template: 'Схема шаблона',
  protocol: 'Протокол в системе',
  // 030: ссылка входа, «Первое письмо», запрос «Проверить связь упоминания».
  login_link: 'Ссылка входа',
  first_letter: '«Первое письмо»',
  link_check: 'Проверка связи упоминания',
  // 033: стоп-кран и рассылка протокола.
  mail_safety: 'Стоп-кран сообщений',
  protocol_mailing: 'Рассылка протокола',
  protocol_meeting: 'День планёрки',
  // 032: ответ из письма, отписка, отъезд, копия руководителю.
  answer_link: 'Личная ссылка',
  letter_answer: 'Ответ из письма',
  unsubscribe: 'Отписка',
  absence: 'Отъезд',
  manager_copy: 'Копия руководителю',
};

export function entityTypeLabel(type: string): string {
  return entityTypeLabels[type] ?? type;
}

/** Запись словами: «Пользователь: Морозов О. С.»; без названия — только тип. */
export function auditEntityLabel(entity: AuditEntry['entity']): string {
  const type = entityTypeLabel(entity.type);
  return entity.title ? `${type}: ${entity.title}` : type;
}

/** Поля «было — стало» в журнале. */
export const fieldLabels: Readonly<Record<string, string>> = {
  fullName: 'ФИО',
  email: 'Почта для входа',
  state: 'Состояние',
  role: 'Роль',
  areas: 'Области',
  assignments: 'Роли',
  validFrom: 'С',
  validTo: 'По',
  validUntil: 'До',
  lockedUntil: 'Заблокирован до',
  archivedAt: 'В архиве с',
  reason: 'Причина',
  hours: 'Часов',
  purpose: 'Ссылка',
  expiresAt: 'Действует до',
  count: 'Число',
  // 0.22.5: «Не он» — упоминания на разбор; «Отменить» — связаны снова.
  mentions: 'Упоминаний',
  userId: 'Пользователь',
  deputyUserId: 'Заместитель',
  supportUserId: 'Сопровождение',
  assignmentId: 'Назначение',
  ownerUserId: 'Владелец роли',
  sessionsRevoked: 'Завершено сеансов',
  inviteExpiresAt: 'Ссылка действует до',
  // 035: создание пользователя с приветственным письмом и «Отправить письмо заново».
  welcome: 'Приветственное письмо',
  letterId: 'Письмо',
  revokedAt: 'Прекращён',
  filters: 'Условия',
  // Условия выгрузки журнала (audit.exported).
  q: 'Поиск',
  aboutUser: 'О пользователе',
  actor: 'Автор',
  operation: 'Действие',
  entityType: 'Тип записи',
  entityId: 'Запись',
  // Повтор и отмена фоновой задачи (job.retried, job.retry_undone, job.cancelled).
  jobType: 'Тип задачи',
  // 008: подключение почты.
  platform: 'Почтовая платформа',
  senderName: 'Имя отправителя',
  senderAddress: 'Адрес отправителя',
  replyTo: 'Адрес для ответов',
  signature: 'Подпись',
  contact: 'Контакт для вопросов',
  keyTail: 'Ключ Resend',
  enabled: 'Отправка включена',
  trackingOff: 'Отслеживание выключено',
  jobOperation: 'Операция задачи',
  from: 'С',
  to: 'По',
  result: 'Итог',
  // Загрузка документа (document.created, document.version_uploaded).
  number: 'Номер',
  title: 'Название',
  document: 'Документ',
  fileName: 'Файл',
  sheetName: 'Лист',
  sheetDate: 'Дата листа',
  sha256: 'Контрольная сумма',
  // Справочник, настройки, ключи агента (005).
  name: 'Название',
  kind: 'Вид',
  side: 'Своя или внешняя',
  externalType: 'Тип организации',
  receivesMail: 'Получает письма',
  notAddressee: 'Не адресат',
  person: 'Человек',
  organization: 'Организация',
  position: 'Должность',
  primary: 'Основное',
  address: 'Адрес',
  external: 'Внешний',
  confirmedAt: 'Подтверждён',
  legalEntity: 'Юрлицо',
  text: 'Текст',
  scope: 'Контекст',
  record: 'Запись',
  answer: 'Ответ',
  main: 'Главная запись',
  merged: 'Вторая запись',
  moved: 'Перенесено',
  undoneAt: 'Разъединено',
  key: 'Настройка',
  value: 'Значение',
  user: 'Пользователь',
  expiresOn: 'Действует по',
  // Оргструктура и массовые действия (006).
  noEmail: 'Без почты',
  unit: 'Подразделение',
  parent: 'Родитель',
  confirmed: 'Подтверждена',
  archiveId: 'Номер архива',
  units: 'Подразделений',
  action: 'Действие',
  done: 'Сделано',
  skipped: 'Пропущено',
  undone: 'Возвращено',
};

export function fieldLabel(key: string): string {
  return fieldLabels[key] ?? key;
}

/** Поля, где значение — номер пользователя: показываются ФИО, если оно известно. */
export const userIdFields: ReadonlySet<string> = new Set([
  'userId',
  'deputyUserId',
  'supportUserId',
  'ownerUserId',
  'actor',
  'aboutUser',
  'entityId',
]);

/** Причины в записях журнала о завершении сеансов. */
export const reasonLabels: Readonly<Record<string, string>> = {
  admin_revoked: 'завершение администратором',
  logout_all: 'выход на всех устройствах',
};

export const purposeLabels: Readonly<Record<string, string>> = {
  invite: 'приглашение',
  reset: 'сброс пароля',
};

export const userStateLabels = {
  invited: 'Приглашён',
  active: 'Активен',
  locked: 'Заблокирован',
  archived: 'В архиве',
} as const satisfies Record<UserState, string>;

/** Состояние замещения словом (contracts/ui.md, «/substitutions»). */
export const substitutionStateLabels = {
  planned: 'Запланировано',
  active: 'Действует',
  ended: 'Закончилось',
  cancelled: 'Отменено',
} as const satisfies Record<SubstitutionState, string>;

/** Состояние временного доступа сопровождения словом. */
export const supportGrantStateLabels = {
  active: 'Действует',
  expired: 'Закончился',
  revoked: 'Прекращён',
} as const satisfies Record<SupportGrantState, string>;

export const areaKindLabels = {
  installation: 'Вся установка',
  legal_entity: 'Юрлицо группы',
  object: 'Объект',
  document_type: 'Тип документа',
  document: 'Документ',
} as const satisfies Record<AreaKind, string>;
